import { FirebaseError } from 'firebase/app'
import { getFunctions, httpsCallable } from 'firebase/functions'
import app from './firebase'
import type { PwfEmployeeTimeSpent } from '../types'

export interface GetPwfEmployeeTimeTrackedInRangeResponse {
  success: boolean
  data: PwfEmployeeTimeSpent[]
  recordedAt: string
}

export interface GetPwfEmployeeTimeByDayInRangeResponse {
  success: boolean
  byDay: Record<string, PwfEmployeeTimeSpent[]>
  recordedAt: string
}

function friendlyPwfError(err: unknown, fnName: string): string {
  if (err instanceof FirebaseError) {
    if (err.code === 'functions/not-found') {
      return `${fnName} is not deployed yet. Fetch from PWF to refresh the snapshot, or deploy: firebase deploy --only functions:${fnName}`
    }
    if (err.code === 'functions/unauthenticated') {
      return 'Sign in to load ProWorkflow time data.'
    }
    if (err.code === 'functions/internal') {
      const detail = err.message?.trim()
      if (detail && detail.toLowerCase() !== 'internal') return detail
      return `ProWorkflow time request failed. Fetch from PWF to refresh the snapshot, or deploy ${fnName}.`
    }
    if (err.message) return err.message
  }
  if (err instanceof Error) {
    const msg = err.message
    if (msg.includes('not-found') || msg.includes('NOT_FOUND')) {
      return `${fnName} is not deployed yet. Fetch from PWF to refresh the snapshot.`
    }
    if (msg.includes('unauthenticated') || msg.includes('UNAUTHENTICATED')) {
      return 'Sign in to load ProWorkflow time data.'
    }
    return msg
  }
  return 'Failed to load tracked time from ProWorkflow'
}

/** Hours logged in PWF between trackedFrom and trackedTo (inclusive, yyyy-mm-dd). */
export async function fetchEmployeeTimeTrackedInRange(
  trackedFrom: string,
  trackedTo: string
): Promise<PwfEmployeeTimeSpent[]> {
  const functions = getFunctions(app)
  const getPwfEmployeeTimeTrackedInRange = httpsCallable<
    { from: string; to: string },
    GetPwfEmployeeTimeTrackedInRangeResponse
  >(functions, 'getPwfEmployeeTimeTrackedInRange', { timeout: 120_000 })

  try {
    const result = await getPwfEmployeeTimeTrackedInRange({
      from: trackedFrom,
      to: trackedTo,
    })
    const data = result.data
    if (!data?.success || !Array.isArray(data.data)) {
      throw new Error('ProWorkflow returned an unexpected time response')
    }
    return data.data
  } catch (err: unknown) {
    throw new Error(friendlyPwfError(err, 'getPwfEmployeeTimeTrackedInRange'))
  }
}

/** Per-day hours logged in PWF between from and to (inclusive, yyyy-mm-dd). */
export async function fetchEmployeeTimeByDayInRange(
  trackedFrom: string,
  trackedTo: string
): Promise<Record<string, PwfEmployeeTimeSpent[]>> {
  const functions = getFunctions(app)
  const getPwfEmployeeTimeByDayInRange = httpsCallable<
    { from: string; to: string },
    GetPwfEmployeeTimeByDayInRangeResponse
  >(functions, 'getPwfEmployeeTimeByDayInRange', { timeout: 180_000 })

  try {
    const result = await getPwfEmployeeTimeByDayInRange({
      from: trackedFrom,
      to: trackedTo,
    })
    const data = result.data
    if (!data?.success || !data.byDay || typeof data.byDay !== 'object') {
      throw new Error('ProWorkflow returned an unexpected by-day time response')
    }
    return data.byDay
  } catch (err: unknown) {
    throw new Error(friendlyPwfError(err, 'getPwfEmployeeTimeByDayInRange'))
  }
}
